// client/src/components/agendamentos/AgendaColunasView.js
import React, { useMemo } from 'react';
import ColunaProfissional from './ColunaProfissional';
import { Loader2, User, Box, Briefcase } from 'lucide-react';
import { isBefore } from 'date-fns';

const PIXELS_POR_MINUTO = 2;

const parseDateSafe = (d) => {
    if (!d) return new Date();
    if (typeof d === 'string' || typeof d === 'number') return new Date(d);
    if (d.$date) return new Date(d.$date);
    return new Date(d);
};

const calcularLayoutEventos = (eventos) => {
    if (!eventos || eventos.length === 0) return [];
    
    const eventosProcessados = eventos.map(evento => ({
        ...evento,
        inicioDate: parseDateSafe(evento._renderDataInicio || evento.dataInicio),
        fimDate: parseDateSafe(evento._renderDataFim || evento.dataFim)
    }))
    .sort((a, b) => a.inicioDate - b.inicioDate || (b.fimDate - a.fimDate));
    
    for (let i = 0; i < eventosProcessados.length; i++) {
        const eventoAtual = eventosProcessados[i];
        if (eventoAtual.coluna !== undefined) continue;
        
        eventoAtual.coluna = 0;
        eventoAtual.totalColunas = 1;
        const conflitos = [eventoAtual];
        
        for (let j = i + 1; j < eventosProcessados.length; j++) {
            const proximoEvento = eventosProcessados[j];
            if (isBefore(proximoEvento.inicioDate, eventoAtual.fimDate) && 
                isBefore(eventoAtual.inicioDate, proximoEvento.fimDate)) {
                conflitos.push(proximoEvento);
            }
        }
        
        if (conflitos.length > 1) {
            conflitos.sort((a, b) => a.inicioDate - b.inicioDate);
            const colunasOcupadas = [];
            
            conflitos.forEach(eventoConflitante => {
                let colunaAtribuida = -1;
                for (let col = 0; col < conflitos.length; col++) {
                    if (!colunasOcupadas[col] || 
                        !isBefore(eventoConflitante.inicioDate, colunasOcupadas[col])) {
                        colunaAtribuida = col;
                        break;
                    }
                }
                
                eventoConflitante.coluna = colunaAtribuida;
                colunasOcupadas[colunaAtribuida] = eventoConflitante.fimDate;
            });
            
            const totalColunasNoGrupo = Math.max(...conflitos.map(e => e.coluna)) + 1;
            conflitos.forEach(e => e.totalColunas = totalColunasNoGrupo);
        }
    }
    return eventosProcessados;
};

const HorariosRuler = ({ intervaloGrade, horaInicio, horaFim }) => {
    const slots = [];
    const minutoInicial = horaInicio * 60;
    const minutoFinal = horaFim * 60;
    
    for (let minuto = minutoInicial; minuto < minutoFinal; minuto += intervaloGrade) {
        const hora = Math.floor(minuto / 60);
        const min = minuto % 60;
        slots.push({
            texto: `${String(hora).padStart(2, '0')}:${String(min).padStart(2, '0')}`,
            altura: `${intervaloGrade * PIXELS_POR_MINUTO}px`,
            isHoraCheia: min === 0
        });
    }
    
    return (
        <div className="ruler-horarios">
            {slots.map(slot => (
                <div key={slot.texto} className="ruler-hora-slot" style={{ height: slot.altura }}>
                    <span className={`ruler-hora-texto ${slot.isHoraCheia ? 'is-hora-cheia' : ''}`}>
                        {slot.texto}
                    </span>
                </div>
            ))}
        </div>
    );
};

const AgendaColunasView = ({ 
    dadosAgrupados, 
    loading, 
    onEventClick, 
    intervaloGrade = 60, 
    horaInicio = 0, 
    horaFim = 24, 
    clientes, 
    recursos,
    groupBy
}) => {
    const dadosComLayout = useMemo(() => {
        if (!dadosAgrupados) return [];
        return dadosAgrupados.map(grupo => ({
            ...grupo,
            eventos: calcularLayoutEventos(grupo.eventos)
        }));
    }, [dadosAgrupados]);

    if (loading) {
        return <div className="panel-state-wrapper"><Loader2 className="animate-spin text-primary"/></div>;
    }
    
    if (!dadosComLayout || dadosComLayout.length === 0) {
        return <div className="panel-state-wrapper panel-info">Nenhum evento para esta data ou filtro.</div>;
    }

    const estiloBodyDaColuna = {
        backgroundImage: `linear-gradient(to bottom, #e2e8f0 1px, transparent 1px)`,
        backgroundSize: `100% ${intervaloGrade * PIXELS_POR_MINUTO}px`
    };

    return (
        <div className="agenda-colunas-container">
            <div className="agenda-timeline">
                <div className="timeline-header-corner">
                    <div className="group-type-icon">
                        {groupBy === 'profissional' && <User size={18} />}
                        {groupBy === 'recurso' && <Box size={18} />}
                        {groupBy === 'servico' && <Briefcase size={18} />}
                    </div>
                </div>
                <HorariosRuler 
                    intervaloGrade={intervaloGrade} 
                    horaInicio={horaInicio} 
                    horaFim={horaFim} 
                />
            </div>
            <div className="agenda-scroll-pane">
                <div className="pane-header-wrapper">
                    {dadosComLayout.map(({ grupo }) => (
                        <div className="coluna-header" key={grupo.id}>
                            {grupo.imagemUrl && !grupo.imagemUrl.includes('placeholder') && !grupo.imagemUrl.includes('default-service') ? (
                                <img src={grupo.imagemUrl} alt={grupo.nome} className="coluna-header-photo" />
                            ) : (
                                <div className="coluna-header-icon">
                                    {grupo.tipo === 'profissional' && <User size={18} />}
                                    {grupo.tipo === 'recurso' && <Box size={18} />}
                                    {grupo.tipo === 'servico' && <Briefcase size={18} />}
                                    {grupo.tipo === 'geral' && <div />}
                                </div>
                            )}
                            <h3 className="coluna-header-name">{grupo.nome}</h3>
                        </div>
                    ))}
                </div>
                <div className="pane-body-wrapper">
                    {dadosComLayout.map(({ grupo, eventos }) => (
                        <ColunaProfissional 
                            key={grupo.id} 
                            eventos={eventos} 
                            onEventClick={onEventClick}
                            bodyStyle={estiloBodyDaColuna} 
                            pixelsPorMinuto={PIXELS_POR_MINUTO}
                            horaInicio={horaInicio}
                            horaFim={horaFim}
                            clientes={clientes}
                            recursos={recursos}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AgendaColunasView;